import React, {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {getAiAgentContext} from '../../../../redux/aiAgent/aiAgentActions';
import {isIntentAvailableInScope} from '../../utils/agentSchema';
import {useFilePickerPending} from '../../hooks/useFilePickerPending';
import {AI_AGENT_SUGGESTIONS} from '../../constants';
import AiAgentSuggestionChip, {type AiAgentSuggestionItem} from './AiAgentSuggestionChip';
import * as css from './AiAgentSuggestions.css';

interface AiAgentSuggestionsProps {
    /** Fills the prompt box — the suggestion is a starting point, not a command. */
    onSuggestionFill: (prompt: string, index: number) => void;
    /** Same picker the prompt attach button opens — no second file input. */
    onOpenFilePicker: () => void;
    /** The explanation is shown above the prompt box, where the user is about to type. */
    onPromptHintChange: (hint: string | null) => void;
}

function AiAgentSuggestions({onSuggestionFill, onOpenFilePicker, onPromptHintChange}: AiAgentSuggestionsProps) {
    const {t} = useTranslation('aiAgent');
    // The scope comes from the pathname, so the offered actions follow the current page.
    const {pathname} = useLocation();
    const {pending: filePickerPending, startPending} = useFilePickerPending();

    const visibleSuggestions = useMemo(() => {
        const {scope} = getAiAgentContext();
        return AI_AGENT_SUGGESTIONS
            .map((suggestion, catalogIndex) => ({...suggestion, catalogIndex}))
            .filter(({intent}) => isIntentAvailableInScope(intent, scope));
    }, [pathname]);

    // The hint belongs to the empty state — it goes when the chips go.
    useEffect(() => () => onPromptHintChange(null), [onPromptHintChange]);

    const handleSelect = useCallback((suggestion: AiAgentSuggestionItem) => {
        const {promptKey, descriptionKey, catalogIndex, opensFilePicker} = suggestion;
        onSuggestionFill(t(promptKey), catalogIndex);
        onPromptHintChange(t(descriptionKey));
        if(opensFilePicker) {
            startPending();
            onOpenFilePicker();
        }
    }, [onOpenFilePicker, onPromptHintChange, onSuggestionFill, startPending, t]);

    return (
        <css.suggestions>
            {visibleSuggestions.map(suggestion => (
                <AiAgentSuggestionChip
                    key={suggestion.labelKey}
                    suggestion={suggestion}
                    loading={!!suggestion.opensFilePicker && filePickerPending}
                    onSelect={handleSelect}
                />
            ))}
        </css.suggestions>
    );
}

export default AiAgentSuggestions;
